import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { api, errMsg } from "../lib/api";
import { useShop } from "../lib/store";
import { chf, money } from "../lib/format";
import { Seo, Loading, Empty } from "../components/Common";
import { useQuote, Totals } from "./Cart";

const EMPTY = { first_name: "", last_name: "", company: "", street: "", postcode: "", city: "", country: "CH", phone: "" };

const AddressFields = ({ prefix, value, onChange, errors }) => {
  const { t } = useI18n();
  const f = (k, label, props = {}) => (
    <div className={props.wide ? "sm:col-span-2" : ""}>
      <label className="label" htmlFor={`${prefix}-${k}`}>{label}</label>
      <input id={`${prefix}-${k}`} className={`field ${errors[`${prefix}.${k}`] ? "field-err" : ""}`} value={value[k] || ""} onChange={(e) => onChange({ ...value, [k]: e.target.value })} data-testid={`${prefix}-${k}-input`} {...(props.input || {})} />
      {errors[`${prefix}.${k}`] && <p className="text-xs text-[color:var(--hw-sale)] mt-1" role="alert" data-testid={`${prefix}-${k}-error`}>{errors[`${prefix}.${k}`]}</p>}
    </div>
  );
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {f("first_name", t("first_name"))}{f("last_name", t("last_name"))}{f("company", t("company"), { wide: true })}{f("street", t("street"), { wide: true })}
      {f("postcode", t("postcode"), { input: { inputMode: "numeric", maxLength: 4 } })}{f("city", t("city"))}
      <div><label className="label" htmlFor={`${prefix}-country`}>{t("country")}</label><select id={`${prefix}-country`} className="field" value={value.country} onChange={(e) => onChange({ ...value, country: e.target.value })} data-testid={`${prefix}-country-select`}><option value="CH">Schweiz / Suisse / Svizzera</option><option value="LI">Liechtenstein</option></select></div>
      {f("phone", t("phone"), { input: { type: "tel" } })}
    </div>
  );
};

export function computeCheckoutErrors({ email, billing, delivery, same, terms }, t) {
  const e = {};
  if (!/^\S+@\S+\.\S+$/.test(email)) e.email = t("invalid_email");
  const chk = (p, a) => {
    ["first_name", "last_name", "street", "postcode", "city"].forEach((k) => { if (!a[k]?.trim()) e[`${p}.${k}`] = t("required"); });
    if (a.postcode && !validPc(a)) e[`${p}.postcode`] = t("invalid_postcode");
  };
  chk("billing", billing);
  if (!same) chk("delivery", delivery);
  if (!terms) e.terms = t("terms_required");
  return e;
}

// Errors appear after the first submit and update live (incl. language switch) as fields change.
const useCheckoutValidation = (form, t) => {
  const [tried, setTried] = useState(false);
  const errors = useMemo(() => (tried ? computeCheckoutErrors(form, t) : {}), [tried, form, t]);
  const validate = useCallback(() => { setTried(true); return Object.keys(computeCheckoutErrors(form, t)).length === 0; }, [form, t]);
  return { errors, validate };
};

const validPc = (a) => /^\d{4}$/.test(a.postcode) && (a.country === "LI" ? +a.postcode >= 9485 && +a.postcode <= 9498 : +a.postcode >= 1000 && +a.postcode <= 9699);

export default function Checkout() {
  const { t, lang } = useI18n();
  const { cart, me } = useShop();
  const coupon = sessionStorage.getItem("hw_coupon") || null;
  const [method, setMethod] = useState(() => sessionStorage.getItem("hw_ship") || null);
  const q = useQuote({ coupon, shipping_method: method });
  const [email, setEmail] = useState("");
  const [billing, setBilling] = useState(EMPTY);
  const [delivery, setDelivery] = useState(EMPTY);
  const [same, setSame] = useState(true);
  const [notes, setNotes] = useState("");
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [serverErr, setServerErr] = useState("");
  const form = useMemo(() => ({ email, billing, delivery, same, terms }), [email, billing, delivery, same, terms]);
  const { errors, validate } = useCheckoutValidation(form, t);
  useEffect(() => {
    if (!me) return;
    setEmail((e) => e || me.email || "");
    if (me.addresses?.[0]) setBilling((b) => (b.street ? b : { ...EMPTY, ...me.addresses[0] }));
  }, [me]);

  if (!cart.length) return <div className="container-hw"><Empty title={t("cart_empty")} action={<Link to="/products" className="btn-primary">{t("continue_shopping")}</Link>} /></div>;
  if (!q) return <Loading />;
  const clean = (a) => Object.fromEntries(Object.entries(a).map(([k, v]) => [k, v === "" ? null : v]));
  const submit = async (ev) => {
    ev.preventDefault(); setServerErr("");
    if (!validate()) { document.querySelector("[role=alert]")?.scrollIntoView({ block: "center" }); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/checkout", {
        items: cart.map(({ sku, qty }) => ({ sku, qty })), coupon, shipping_method: q.shipping_method, lang, email,
        billing: clean(billing), delivery: same ? null : clean(delivery), notes: notes || null, accept_terms: terms, origin_url: window.location.origin,
      });
      sessionStorage.setItem("hw_pending_order", data.order_number);
      window.location.href = data.checkout_url;
    } catch (e) { setServerErr(errMsg(e)); setBusy(false); }
  };
  return (
    <div className="container-hw py-10" data-testid="checkout-page">
      <Seo title={t("checkout_title")} noindex />
      <h1 className="text-4xl sm:text-5xl mb-8">{t("checkout_title")}</h1>
      <form onSubmit={submit} noValidate className="grid lg:grid-cols-[1fr_400px] gap-10">
        <div className="space-y-10">
          <section>
            <h2 className="text-2xl mb-1">{t("contact")}</h2>
            {!me && <p className="text-sm text-[color:var(--hw-muted)] mb-4">{t("guest_note")} <Link to="/sign-in" className="link-u" data-testid="checkout-sign-in-link">{t("sign_in")}</Link></p>}
            <label className="label" htmlFor="email">{t("email")}</label>
            <input id="email" type="email" className={`field ${errors.email ? "field-err" : ""}`} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="checkout-email-input" />
            {errors.email && <p className="text-xs text-[color:var(--hw-sale)] mt-1" role="alert" data-testid="checkout-email-error">{errors.email}</p>}
          </section>
          <section><h2 className="text-2xl mb-4">{t("billing")}</h2><AddressFields prefix="billing" value={billing} onChange={setBilling} errors={errors} /></section>
          <section>
            <label className="flex items-center gap-2 text-sm mb-4"><input type="checkbox" checked={same} onChange={(e) => setSame(e.target.checked)} data-testid="checkout-same-address" />{t("same_address")}</label>
            {!same && <><h2 className="text-2xl mb-4">{t("delivery_address")}</h2><AddressFields prefix="delivery" value={delivery} onChange={setDelivery} errors={errors} /></>}
          </section>
          <section>
            <h2 className="text-2xl mb-4">{t("shipping_method")}</h2>
            {q.shipping_methods.map((m) => (
              <label key={m.id} className={`flex items-center gap-3 border p-4 mb-2 text-sm cursor-pointer ${q.shipping_method === m.id ? "border-[color:var(--hw-ink)] bg-white" : "border-[color:var(--hw-line)]"} ${m.available ? "" : "opacity-50"}`}>
                <input type="radio" name="m" disabled={!m.available} checked={q.shipping_method === m.id} onChange={() => setMethod(m.id)} data-testid={`checkout-shipping-${m.id}`} />
                <span className="flex-1">{m.name}<span className="block text-xs text-[color:var(--hw-muted)]">{m.available ? t("delivery_in", { a: m.days[0], b: m.days[1] }) : t("not_available_heavy")}</span></span><span className="font-semibold">{chf(m.fee)}</span>
              </label>
            ))}
            <label className="label mt-4" htmlFor="notes">{t("notes")}</label>
            <textarea id="notes" className="field h-20 py-2" maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="checkout-notes-input" />
          </section>
        </div>
        <aside className="bg-white border border-[color:var(--hw-line)] p-6 h-fit lg:sticky lg:top-32 space-y-4" data-testid="checkout-summary">
          <h2 className="text-2xl">{t("order_summary")}</h2>
          <ul className="space-y-3 max-h-72 overflow-auto">{q.lines.map((l) => <li key={l.sku} className="flex gap-3 text-sm"><img src={l.image} alt="" className="w-14 h-16 object-cover" /><span className="flex-1">{l.qty} × {l.name}<span className="block text-xs text-[color:var(--hw-muted)]">{l.sku}</span></span><span>{chf(l.line_total)}</span></li>)}</ul>
          <Totals q={q} />
          {q.errors.length > 0 && <p className="text-sm text-[color:var(--hw-sale)]" role="alert">{t("stock_issue", { n: q.errors[0].available ?? 0 })} <Link to="/cart" className="link-u">{t("back_to_cart")}</Link></p>}
          <label className="flex items-start gap-2 text-xs"><input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5" data-testid="checkout-terms-checkbox" /><span>{t("accept_terms")} <Link to="/terms" className="link-u" target="_blank">{t("terms")}</Link> · <Link to="/privacy" className="link-u" target="_blank">{t("privacy")}</Link></span></label>
          {errors.terms && <p className="text-xs text-[color:var(--hw-sale)]" role="alert" data-testid="checkout-terms-error">{errors.terms}</p>}
          {serverErr && <p className="text-sm text-[color:var(--hw-sale)]" role="alert" data-testid="checkout-server-error">{t("error")} ({serverErr})</p>}
          <button className="btn-primary w-full h-12" disabled={busy || q.errors.length > 0} data-testid="checkout-submit-button">{busy ? <><Loader2 size={16} className="animate-spin" />{t("redirecting")}</> : `${t("pay_now")} · ${chf(q.total)}`}</button>
          <p className="text-xs text-[color:var(--hw-muted)]">{t("pay_note")}</p>
        </aside>
      </form>
    </div>
  );
}

export const CheckoutResult = ({ kind }) => {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const { clearCart, isSignedIn } = useShop();
  const [state, setState] = useState({ status: "pending" });
  const sid = params.get("session_id");
  const oid = params.get("order");
  useEffect(() => {
    let n = 0, stop = false;
    if (kind === "cancel") {
      if (oid) api.post(`/orders/${oid}/cancel-payment`).then((r) => setState({ status: r.data.payment_status === "paid" ? "paid" : "cancelled", number: r.data.number })).catch(() => setState({ status: "cancelled" }));
      else setState({ status: "cancelled" });
      return;
    }
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState({ status: "paid", order: data.order }); clearCart(); sessionStorage.removeItem("hw_coupon"); return; }
        if (["failed", "expired", "cancelled"].includes(data.payment_status)) { setState({ status: "failed" }); return; }
      } catch (e) { console.warn("Payment status check failed, retrying", e); }
      if (++n < 15) setTimeout(poll, 2000); else setState({ status: "timeout" });
    };
    poll();
    return () => { stop = true; };
  }, [kind, sid, oid, clearCart]);
  const o = state.order;
  return (
    <div className="container-hw py-20 max-w-2xl" data-testid="checkout-result">
      <Seo title={t("order_summary")} noindex />
      {state.status === "pending" && <div className="text-center" data-testid="payment-pending"><Loader2 className="animate-spin mx-auto mb-4" />{t("payment_pending")}</div>}
      {state.status === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 size={44} className="text-[color:var(--hw-green)] mb-4" strokeWidth={1.4} />
          <h1 className="text-4xl sm:text-5xl mb-3">{t("payment_success")}</h1>
          {o && <>
            <p className="text-sm mb-1">{t("order_number")}: <strong data-testid="order-number">{o.number}</strong></p>
            <p className="text-sm text-[color:var(--hw-muted)] mb-8">{t("confirmation_email", { e: o.email })}</p>
            <ul className="border-y border-[color:var(--hw-line)] divide-y divide-[color:var(--hw-line)] mb-4">{o.items.map((i) => <li key={i.sku} className="py-3 flex justify-between text-sm"><span>{i.qty} × {i.name} <span className="text-[color:var(--hw-muted)]">({i.sku})</span></span><span>{money(i.line_total, o.currency || "CHF", o.fx_rate || 1)}</span></li>)}</ul>
            <Totals q={{ ...o, bundles: [], vat_rate: 8.1 }} fmt={(x) => money(x, o.currency || "CHF", o.fx_rate || 1)} />
          </>}
          <div className="flex flex-wrap gap-3 mt-8">{isSignedIn && <Link to="/account" className="btn-primary">{t("my_orders")}</Link>}<Link to="/track-order" className="btn-outline">{t("track_order")}</Link><Link to="/contact" className="btn-ghost">{t("support")}</Link></div>
        </div>
      )}
      {["failed", "cancelled", "timeout"].includes(state.status) && (
        <div data-testid={`payment-${state.status}`}>
          <XCircle size={44} className="text-[color:var(--hw-sale)] mb-4" strokeWidth={1.4} />
          <h1 className="text-4xl mb-3">{state.status === "cancelled" ? t("payment_cancelled") : state.status === "timeout" ? t("payment_pending") : t("payment_failed")}</h1>
          <div className="flex gap-3 mt-6"><Link to="/cart" className="btn-primary" data-testid="result-back-to-cart">{t("back_to_cart")}</Link><Link to="/contact" className="btn-outline">{t("support")}</Link></div>
        </div>
      )}
    </div>
  );
};
